import { lazy, Suspense, useEffect, useRef } from 'react';
import { useUIStore } from '@/stores/ui-store';
import { useModuleStore } from '@/stores/module-store';
import { useAppStore } from '@/stores/app-store';
import { useCallStore } from '@/stores/call-store';
import { ConversationsView } from '@/components/conversations/conversations-view';
import { CredentialEntryDialog } from '@/components/common/credential-entry-dialog';
import { AuthTokenDialog } from '@/components/common/auth-token-dialog';
import { TPAAuthPrompt } from '@/components/auth/tpa-auth-prompt';
import { useOrgSetupGateVisible } from '@/stores/org-join-store';
import { AppSkeleton } from '@/components/common/app-skeleton';
import { EVENT_APPS, CALL_APPS } from '@/config/constants';

// Retry wrapper — on chunk load failure (e.g. stale hash after deploy), reload the page once
function lazyWithRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((err) => {
      const reloaded = sessionStorage.getItem('chunk_reload');
      if (!reloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      throw err; // second failure — let error boundary handle it
    })
  );
}

// Lazy-loaded views — only fetched when activeView matches
const ComposeView = lazyWithRetry(() => import('@/components/messaging/compose-view').then(m => ({ default: m.ComposeView })));
const CrmChatView = lazyWithRetry(() => import('@/components/conversations/crm-chat-view').then(m => ({ default: m.CrmChatView })));
const KanbanView = lazyWithRetry(() => import('@/components/conversations/kanban-view').then(m => ({ default: m.KanbanView })));
// SettingsView is now imported and rendered directly inside app-layout.tsx
// as an overlay, so app-page.tsx no longer needs its own import.
const EventsView = lazyWithRetry(() => import('@/components/events/events-view').then(m => ({ default: m.EventsView })));
const GoogleDriveView = lazyWithRetry(() => import('@/components/google-drive/google-drive-view').then(m => ({ default: m.GoogleDriveView })));
const StripeView = lazy(() => import('@/components/stripe/stripe-view').then(m => ({ default: m.StripeView })));
const RecurringTasksView = lazyWithRetry(() => import('@/components/recurring-tasks/recurring-tasks-view').then(m => ({ default: m.RecurringTasksView })));
const RecurringTasksPanelView = lazyWithRetry(() => import('@/components/recurring-tasks/recurring-tasks-panel-view').then(m => ({ default: m.RecurringTasksPanelView })));
const LookupView = lazyWithRetry(() => import('@/components/lookup/lookup-view').then(m => ({ default: m.LookupView })));
const CallCenterView = lazyWithRetry(() => import('@/components/voice/call-center-view').then(m => ({ default: m.CallCenterView })));
const PhoneTreeBuilder = lazyWithRetry(() => import('@/components/phone-tree/phone-tree-builder').then(m => ({ default: m.PhoneTreeBuilder })));
// ChatAutomationBuilder is rendered fullscreen in app-layout.tsx now.
const CrmCallManager = lazyWithRetry(() => import('@/components/voice/crm-call-manager').then(m => ({ default: m.CrmCallManager })));

/** Prefetch lazy chunks when the browser is idle so view switches are instant */
function usePrefetchViews() {
  const prefetched = useRef(false);
  useEffect(() => {
    if (prefetched.current) return;
    prefetched.current = true;
    const idle = typeof requestIdleCallback === 'function' ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 3000);
    idle(() => {
      import('@/components/settings/settings-view').catch(() => {});
    });
  }, []);
}

export function AppPage() {
  usePrefetchViews();
  const activeView = useUIStore((s) => s.activeView);
  const viewResolved = useUIStore((s) => s.viewResolved);
  const setView = useUIStore((s) => s.setView);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const tpaAuthChecking = useModuleStore((s) => s.tpaAuthChecking);
  const tpaLoading = useModuleStore((s) => s.tpaLoading);
  const saasAuthenticated = useModuleStore((s) => s.saasAuthenticated);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const saasModule = useModuleStore((s) => s.saasModule);

  // Show credential dialog when TPA module is loaded but not authenticated.
  // Gate on saasAuthenticated so the TPA dialog doesn't cover the SaaS auth
  // prompt — SaaS must be authorized first (needed for orgId, settings, etc.)
  // Skip the saas gate for standalone TPAs (no SaaS module, e.g. whatsappforpage)
  const saasReady = saasAuthenticated || !saasModule;
  const orgGateVisible = useOrgSetupGateVisible();
  const showCredentialDialog =
    tpaModule !== null &&
    !tpaAuthenticated &&
    !tpaAuthChecking &&
    !tpaLoading &&
    saasReady &&
    !orgGateVisible &&
    !tpaModule.getOAuthInfo?.();

  const isEventApp = appCodeTPA != null && (EVENT_APPS as readonly string[]).includes(appCodeTPA);
  const isCallApp = appCodeTPA != null && (CALL_APPS as readonly string[]).includes(appCodeTPA);
  const crmCallManagerOpen = useCallStore((s) => s.crmCallManagerOpen);

  const settingsOpen = useUIStore((s) => s.settingsOpen);
  useEffect(() => {
    if (!showCredentialDialog || !isEventApp) return;
    if (settingsOpen || activeView === 'EVENTS') return;
    setView('EVENTS');
  }, [activeView, settingsOpen, isEventApp, setView, showCredentialDialog]);

  // For call apps: open CRM call manager by default unless on /inbox or /calls path
  const callAppInitDone = useRef(false);
  useEffect(() => {
    if (!isCallApp || !viewResolved || callAppInitDone.current) return;
    callAppInitDone.current = true;
    const viewPath = window.location.pathname.split('/').filter(Boolean);
    const isCallCenterPath = viewPath.some(p => p === 'inbox' || p === 'calls');
    if (!isCallCenterPath && !crmCallManagerOpen) {
      useCallStore.getState().toggleCrmCallManager();
    }
  }, [isCallApp, viewResolved, crmCallManagerOpen]);

  return (
    <div className="h-full">
      {!viewResolved ? (
        /* Skeleton shown immediately while modules load */
        <AppSkeleton view={activeView} />
      ) : crmCallManagerOpen ? (
        /* CRM Call Manager — replaces current view when open */
        <Suspense fallback={<AppSkeleton view="CONVERSATIONS" />}><CrmCallManager /></Suspense>
      ) : (
        <>
          {activeView === 'MESSAGE_FORM' && !isCallApp && <Suspense fallback={<AppSkeleton view="MESSAGE_FORM" />}><ComposeView /></Suspense>}
          {activeView === 'CHAT' && !isCallApp && <Suspense fallback={<AppSkeleton view="CHAT" />}><CrmChatView /></Suspense>}
          {activeView === 'CONVERSATIONS' && !isCallApp && <ConversationsView />}
          {activeView === 'KANBAN' && !isCallApp && !crmRecordContext && appCodeTPA === 'whatsapp' && <Suspense fallback={<AppSkeleton view="CONVERSATIONS" />}><KanbanView /></Suspense>}
          {activeView === 'KANBAN' && !isCallApp && !crmRecordContext && appCodeTPA !== 'whatsapp' && <ConversationsView />}
          {activeView === 'KANBAN' && !isCallApp && crmRecordContext && <Suspense fallback={<AppSkeleton view="CONVERSATIONS" />}><CrmChatView /></Suspense>}
          {isCallApp && (activeView === 'CONVERSATIONS' || activeView === 'MESSAGE_FORM' || activeView === 'CHAT') && <Suspense fallback={<AppSkeleton view="CONVERSATIONS" />}><CallCenterView /></Suspense>}
          {activeView === 'PHONE_TREE' && isCallApp && <Suspense fallback={<AppSkeleton view="SETTINGS" />}><PhoneTreeBuilder /></Suspense>}
          {/* CHAT_AUTOMATION rendered as fullscreen in app-layout.tsx (no app Header) */}
          {/* Settings now renders as an overlay in app-layout.tsx, not a view swap. */}
          {activeView === 'EVENTS' && <Suspense fallback={<AppSkeleton view="EVENTS" />}><EventsView /></Suspense>}
          {activeView === 'GOOGLEDRIVE' && <Suspense fallback={<AppSkeleton view="GOOGLEDRIVE" />}><GoogleDriveView /></Suspense>}
          {activeView === 'STRIPE' && <Suspense fallback={<AppSkeleton view="STRIPE" />}><StripeView /></Suspense>}
          {activeView === 'LOOKUP' && <Suspense fallback={<AppSkeleton view="CONVERSATIONS" />}><LookupView /></Suspense>}
          {activeView === 'RECURRING_TASKS' && <Suspense fallback={<AppSkeleton view="RECURRING_TASKS" />}><RecurringTasksView /></Suspense>}
          {activeView === 'RECURRING_TASKS_PANEL' && <Suspense fallback={<AppSkeleton view="RECURRING_TASKS_PANEL" />}><RecurringTasksPanelView /></Suspense>}
        </>
      )}

      {/* TPA Credential Entry Dialog — shown when credentials are missing (API-key TPAs) */}
      <CredentialEntryDialog
        open={showCredentialDialog}
        dismissible
        onClose={() => {
          // Re-check auth — if credentials are still valid, dialog will close
          useModuleStore.getState().checkTPAAuth();
        }}
      />

      {/* TPA OAuth Auth Prompt — shown for OAuth-based TPAs (self-gates internally) */}
      <TPAAuthPrompt />

      {/* Auth Token Dialog — shown when ?ua_target_action=show_tpa_uatoken */}
      <AuthTokenDialog />
    </div>
  );
}
